import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Container,
    Row,
    Col,
    Button,
    Tab,
    Nav,
    Breadcrumb,
} from "react-bootstrap";
import { useLocation, useParams, useHistory } from "react-router-dom";
import LdsuList from "components/gateways/LdsuList";
import SensorList from "components/gateways/SensorList";
import ActuatorList from "components/gateways/actuators/ActuatorList";
import dltWhiteIcon from "assets/svg/delete-white.svg";
import searchIcon from "assets/svg/search.svg";
import {
    getGatewayAlertNotification,
    getRegistryConfig,
    getGwAndLDSUStatus,
    scanGateway,
    resetGateway,
    getAllLastBusReading,
    deleteLDSBus,
    getOneRegistry,
} from "service/gatewayService";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import {
    LDSUTabPanel,
    HttpStatus,
    SCAN_REQUEST_MESSAGE,
    PORT_POWERED_ON_SCAN_MESSAGE,
    PORT_POWERED_OFF_SCAN_MESSAGE,
    PORT_FAULTY_SCAN_MESSAGE,
} from "constant";
import {
    GatewayReading,
    GatewayReadingDevice,
    LDSBus as LDSBusState,
    PortStatus,
    Registry,
    DeviceConfig,
} from "types/Gateways";
import { LoadingModal, useModal } from "components/modals/ModalTemplate";
import { get } from "lodash";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { canAccess } from "utils/authorize-action";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { PowerCycleCheckBox } from "components/gateways/Gateway";
import {
    getGatewayType,
    getLDSBusInfo,
    getPowerStatusText,
    isFaulty,
} from "utils/gatewayFunctions";
import { isHttpSuccess } from "utils/functions";
import { CombinedLdsu, LdsuDto } from "types/Ldsu";
import { LdsActuatorDetails } from "types/Actuator";
import { APP_CONFIG } from "config/appConfig";
import { GatewayType } from "constant/gateway";
import { useDeleteLdsuModal } from "components/modals/DeleteLdsuModal";

const { FORBIDDEN } = HttpStatus;

type LDSBusLocationState = {
    panel: LDSUTabPanel;
    gatewayName?: string;
    refresh?: boolean;
};

const LDSBus = () => {
    const params: any = useParams();
    const location = useLocation<LDSBusLocationState>();
    const history = useHistory();
    const { busNumber } = params;
    const state: LDSBusLocationState = get(location, "state", {
        panel: LDSUTabPanel.LDSU,
    });
    const activePanel = get(state, "panel", LDSUTabPanel.LDSU);
    const [ldsuLength, setLdsuLength] = useState(0);
    const [sensorLength, setSensorLength] = useState("");
    const [alertUUIDList, setAlertUUIDList] = useState<Array<any>>([]);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [actionStatus, setActionStatus] = useState("idle");
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTitle, setLoadingTitle] = useState("");
    const [ldsBusState, setLdsBusState] = useState<LDSBusState>({
        id: busNumber,
        power: false,
        status: PortStatus.On,
    });

    const [ldsuList, setLdsuList] = useState<Array<CombinedLdsu>>([]);
    const [sensorList, setSensorList] = useState<Array<any>>([]);
    const [actuatorList, setActuatorList] = useState<LdsActuatorDetails[]>([]);

    const [registryDetails, setRegistryDetails] = useState<Registry>();

    const gatewayType: GatewayType = useMemo(() => {
        return registryDetails?.info
            ? getGatewayType(registryDetails.info) || GatewayType.PSU
            : GatewayType.PSU;
    }, [registryDetails?.info]);

    // Modal State
    const { modal, openModal, modalData, setModalData, setModalShow } =
        useModal();
    const { openModal: openDeleteLdsuModal } = useDeleteLdsuModal();

    useEffect(() => {
        setModalData({
            powerCycle: false,
        });
    }, [setModalData]);

    useEffect(() => {
        const fetch = async () => {
            const [
                registryInfoRes,
                registryConfigRes,
                registryGwAndLdsuStatus,
                registryBusLastReading,
                registryAlertList,
            ] = await Promise.all([
                getOneRegistry(params.gatewayId),
                getRegistryConfig(params.gatewayId),
                getGwAndLDSUStatus(params.gatewayId),
                getAllLastBusReading(params.gatewayId, params.busNumber),
                getGatewayAlertNotification(params.gatewayId),
            ]);

            if (
                [
                    registryInfoRes.status,
                    registryConfigRes.status,
                    registryGwAndLdsuStatus.status,
                    registryBusLastReading.status,
                    registryAlertList.status,
                ].includes(FORBIDDEN)
            ) {
                setIsForbiddenResource(true);
                setIsPageLoading(false);
                return;
            }

            if (
                isHttpSuccess(registryConfigRes.status) &&
                isHttpSuccess(registryGwAndLdsuStatus.status) &&
                isHttpSuccess(registryInfoRes.status)
            ) {
                const registry = registryInfoRes.data;
                const config = registryConfigRes.data as DeviceConfig[];
                const gatewayReading =
                    registryGwAndLdsuStatus.data as GatewayReading;

                transformLdsuList(
                    registry?.info?.devices || [],
                    config,
                    gatewayReading.devices,
                    registryAlertList.data
                );

                setRegistryDetails(registry);

                processLdsBusState(registry);
            }
            setIsPageLoading(false);
        };

        if (!canAccess("gateway:read")) {
            setIsPageLoading(false);
            setIsForbiddenResource(true);
        } else {
            fetch();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params.gatewayId,
        busNumber,
        window.authorizationConfig?.gateway,
        actionStatus,
        state.refresh,
    ]);

    const updateLDSULength = useCallback((length: number) => {
        setLdsuLength(length);
    }, []);

    /* eslint-disable */
    const transformAlertList = (lst1: any[], lst2: any[]) => {
        let filteredLst: any[] = [];
        lst1.forEach((i: any) => {
            lst2.some((j: any, index) => {
                if (
                    i.device_id === j.ldsu_uuid &&
                    String(i.said) === String(j.SAID)
                ) {
                    let body = {
                        index: index,
                        uuid: i.uuid,
                        source: j.ldsu_uuid,
                        said: i.said,
                        gatewayUUID: i.gateway_id,
                        status: i.enabled,
                        notice: i.notice,
                    };
                    filteredLst.push(body);
                }
            });
        });
        setAlertUUIDList(filteredLst);
    };

    const transformLdsuList = (
        registryInfoList: LdsuDto[],
        configList: DeviceConfig[],
        gwLDSUStatusList: GatewayReadingDevice[],
        alertList: any
    ) => {
        const { ldsus, sensors, actuators } = getLDSBusInfo(
            busNumber,
            registryInfoList,
            configList,
            gwLDSUStatusList
        );

        setActuatorList(actuators);

        // Map with Alert list
        if (!APP_CONFIG.APP_IS_ONPREM) {
            transformAlertList(alertList, sensors);
        }
        setSensorList(sensors);
        setSensorLength(sensors.length);
        setLdsuList(ldsus);
    };
    /* eslint-enable */

    const processLdsBusState = (data: any) => {
        const power = data.info.bus_power[busNumber - 1];
        const status = data.port?.PORT[busNumber - 1];

        setLdsBusState({
            id: busNumber,
            power,
            status,
        });
    };

    const closeModal = () => {
        setModalShow(false);
        setModalData({});
    };

    const openScanModal = () => {
        const status = getPowerStatusText(
            ldsBusState.power,
            ldsBusState.status
        );

        let modalTitle_ = "";
        let modalMessage_ = "";

        if (["On", "Off"].includes(status)) {
            modalTitle_ = `Scan in Powered ${status} Port`;
            modalMessage_ =
                status === "On"
                    ? PORT_POWERED_ON_SCAN_MESSAGE
                    : PORT_POWERED_OFF_SCAN_MESSAGE;
        } else {
            modalTitle_ = `Scan in Faulty Port`;
            modalMessage_ = PORT_FAULTY_SCAN_MESSAGE;
        }

        const modalContent = (openedModalData: any) => {
            return (
                <>
                    <div className="mb-3"> {modalMessage_}</div>
                    {!isFaulty(ldsBusState.status) && (
                        <PowerCycleCheckBox
                            custom
                            id="power-cycle"
                            className="mb-3"
                            checked={!!openedModalData.powerCycle}
                            onChange={(e) => {
                                setModalData({
                                    ...modalData,
                                    powerCycle: !openedModalData.powerCycle,
                                });
                            }}
                            label={
                                <span className="ml-1">
                                    Scan with power cycle
                                </span>
                            }
                        />
                    )}
                </>
            );
        };

        const modalButton = (openedModalData: any) => {
            return (
                <>
                    <Button variant="danger" onClick={closeModal}>
                        CANCEL
                    </Button>
                    <Button
                        disabled={
                            status === "Off" && !openedModalData.powerCycle
                        }
                        onClick={() => {
                            scanThisLDSBus(openedModalData);
                        }}
                    >
                        SCAN
                    </Button>
                </>
            );
        };

        openModal({
            key: "open_scan_modal",
            modalType: "custom",
            modalIcon: "help_outline",
            iconVariant: "primary",
            modalTitle: modalTitle_,
            modalContent,
            modalButton,
        });
    };

    const handleScanAction = () => {
        if (gatewayType === GatewayType.LoRaWAN) {
            // For LoRaWAN, skip the scan modal and go directly to scanning
            scanThisLDSBus(modalData || {});
        } else {
            // For other gateways, show the scan modal first
            openScanModal();
        }
    };

    const openWarningConfirmationModal = () => {
        openModal({
            key: "handle_warning_confirmation_modal",
            modalType: "custom",
            modalIcon: "help_outline",
            iconVariant: "primary",
            modalTitle: "Disable Related Events",
            modalContent: `A scan request may cause events to be falsely triggered. This message may be ignored if you have already disabled events involving this port.`,
            modalButton: (
                <>
                    <Button variant="danger" onClick={closeModal}>
                        CANCEL
                    </Button>
                    <Button onClick={handleScanAction}>CONTINUE</Button>
                </>
            ),
        });
    };

    const scanThisLDSBus = async (openedModalData?: any) => {
        setIsLoading(true);
        setLoadingTitle("Scanning...");
        const res = await scanGateway(
            params.gatewayId,
            params.busNumber,
            isFaulty(ldsBusState.status) ? true : !!openedModalData.powerCycle
        );
        if (isHttpSuccess(res.status)) {
            // Call config API
            const registryConfigRes = await getRegistryConfig(params.gatewayId);

            if (isHttpSuccess(registryConfigRes.status)) {
                const registryConfig = registryConfigRes.data ?? [];
                // Check if the config has any details.
                if (registryConfig.length === 0) {
                    // call reset API
                    const registryResetRes = await resetGateway(
                        params.gatewayId
                    );
                    if (isHttpSuccess(registryResetRes.status)) {
                        showSuccessAlert({
                            message: SCAN_REQUEST_MESSAGE,
                        });
                        setActionStatus("complete");
                    } else {
                        showErrorAlert(
                            getAPIError(
                                registryResetRes,
                                `Unable to scan. Please try again later.`
                            )
                        );
                    }
                    setModalShow(true);
                } else {
                    showSuccessAlert({
                        message: SCAN_REQUEST_MESSAGE,
                    });
                    setActionStatus("complete");
                }
            }
        } else {
            showErrorAlert(
                getAPIError(
                    res,
                    `Unable to scan LDSUs. Please try again later.`
                )
            );
        }

        setIsLoading(false);

        closeModal();
    };

    const handleOpenDeleteModal = () => {
        const modelData: any = {
            isDeleteAll: true,
        };

        if (gatewayType !== GatewayType.LoRaWAN) {
            modelData.busNumber = Number(busNumber);
        }

        openDeleteLdsuModal({
            onConfirm: deleteThisBus,
            modelData,
        });
    };

    const deleteThisBus = async () => {
        const res: any = await deleteLDSBus(params.gatewayId, params.busNumber);

        if (isHttpSuccess(res.status)) {
            showSuccessAlert({
                message: `${ldsuLength} LDSU(s) are deleted successfully.`,
            });
            history.push(`/gateway-details/${params.gatewayId}`);
        } else {
            showErrorAlert(
                getAPIError(
                    res,
                    `Unable to delete LDSUs. Please try again later.`
                )
            );
        }
    };

    const getPageTitle = () => {
        if (gatewayType === GatewayType.LoRaWAN) {
            return `Modbus`;
        }
        return `LDSBus ${params.busNumber}`;
    };

    const pageTitle = getPageTitle();

    return (
        <ContentWrapper
            isForbiddenResource={isForbiddenResource}
            title={pageTitle}
            isLoading={isPageLoading}
        >
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col
                            sm={6}
                            className="LDSBus-header"
                            data-testid="ldsbus-header"
                        >
                            <Row>
                                <Col>
                                    <h5 className="page-title">{pageTitle}</h5>
                                </Col>
                                <Col sm="12">
                                    <Breadcrumb className="w-100">
                                        <Breadcrumb.Item
                                            href={`/gateway-details/${params.gatewayId}`}
                                        >
                                            Gateway
                                        </Breadcrumb.Item>
                                        <Breadcrumb.Item active>
                                            {pageTitle}
                                        </Breadcrumb.Item>
                                    </Breadcrumb>
                                </Col>
                            </Row>
                        </Col>
                        <Col sm={6} className="ldsbus-buttons-container">
                            {gatewayType !== GatewayType.LoRaWAN && (
                                <div className="ldsbus-buttons">
                                    <HoverAuthorizeTooltip permission="gateway:update">
                                        <Button
                                            variant="primary"
                                            onClick={
                                                openWarningConfirmationModal
                                            }
                                            style={{ width: "130px" }}
                                        >
                                            <span>
                                                <img
                                                    className="mr-2 ml-1"
                                                    src={searchIcon}
                                                    alt="search"
                                                />
                                            </span>
                                            {""}
                                            Scan
                                        </Button>
                                    </HoverAuthorizeTooltip>
                                </div>
                            )}
                            <div className="ldsbus-buttons">
                                <HoverAuthorizeTooltip permission="gateway:delete">
                                    <Button
                                        style={{ width: "130px" }}
                                        variant="danger"
                                        onClick={handleOpenDeleteModal}
                                    >
                                        <span>
                                            <img
                                                className="mr-2 ml-2"
                                                alt="delete"
                                                src={dltWhiteIcon}
                                            />
                                        </span>
                                        {""}
                                        Delete
                                    </Button>
                                </HoverAuthorizeTooltip>
                            </div>
                        </Col>
                    </Row>

                    <Row className="mt-3">
                        <Col>
                            <div className="page-tabs">
                                <Tab.Container
                                    id="left-tabs-example"
                                    defaultActiveKey={activePanel}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav>
                                                <Nav.Item>
                                                    <Nav.Link
                                                        eventKey={
                                                            LDSUTabPanel.LDSU
                                                        }
                                                    >
                                                        <h6>
                                                            LDSUs{" "}
                                                            <span className="count">
                                                                {ldsuLength}
                                                            </span>
                                                        </h6>
                                                    </Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link
                                                        eventKey={
                                                            LDSUTabPanel.Sensor
                                                        }
                                                    >
                                                        <h6>
                                                            Sensors{" "}
                                                            <span className="count">
                                                                {sensorLength}
                                                            </span>
                                                        </h6>
                                                    </Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link
                                                        eventKey={
                                                            LDSUTabPanel.Actuator
                                                        }
                                                    >
                                                        <h6>
                                                            Actuators{" "}
                                                            <span className="count">
                                                                {
                                                                    actuatorList.length
                                                                }
                                                            </span>
                                                        </h6>
                                                    </Nav.Link>
                                                </Nav.Item>
                                            </Nav>
                                        </Col>
                                        <Col sm={12}>
                                            <Tab.Content>
                                                <Tab.Pane eventKey="ldsus">
                                                    <LdsuList
                                                        ldsus={ldsuList}
                                                        setLdsuLength={
                                                            updateLDSULength
                                                        }
                                                        gatewayName={
                                                            state.gatewayName ||
                                                            ""
                                                        }
                                                        portNumber={
                                                            params.busNumber
                                                        }
                                                        gatewayType={
                                                            gatewayType
                                                        }
                                                        UUID={params.gatewayId}
                                                        registryDetails={
                                                            registryDetails
                                                        }
                                                    />
                                                </Tab.Pane>
                                                <Tab.Pane eventKey="sensors">
                                                    <SensorList
                                                        sensors={sensorList}
                                                        setSensorLength={
                                                            setSensorLength
                                                        }
                                                        gatewayName={
                                                            state.gatewayName
                                                        }
                                                        portNumber={
                                                            params.busNumber
                                                        }
                                                        alertUUIDList={
                                                            alertUUIDList
                                                        }
                                                        setAlertUUIDList={
                                                            setAlertUUIDList
                                                        }
                                                        UUID={params.gatewayId}
                                                    />
                                                </Tab.Pane>
                                                <Tab.Pane eventKey="actuators">
                                                    <ActuatorList
                                                        gatewayName={
                                                            state.gatewayName
                                                        }
                                                        actuators={actuatorList}
                                                    />
                                                </Tab.Pane>
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {modal}

            <LoadingModal showModal={isLoading} title={loadingTitle} />
        </ContentWrapper>
    );
};

export default LDSBus;
